import { Fragment, useEffect } from "react";

import BasePage from "./BasePage";
import { useApp } from "./App";
import { servicesOf } from "./service";
import { detectLang, enabledLangs, setDocumentLang } from "./i18n";
import { consentPage, type ConsentParagraph, type ConsentPiece } from "./consentPage";

// The consent page a client opens from the checkbox on the form. Rewritten 2026-09-02 with Fable
// eyes at Chris's word, replacing 2,692 words of 2023 template (Indianapolis courts for every shop,
// EEA transfer clauses, cookie language for a page that has none). Two parties on purpose: the shop,
// who tattoos the client and owns the record, and waivers.tattoo, who runs the form for the shop.
// The marketing consent is intent, not boilerplate (docs/DECISIONS.md 2026-09-02): the client list
// is the shop's to keep in touch with, through us or its own tools; health answers and the ID photo
// never are. No counsel yet; the source marks it as awaiting a legal read when one is affordable.
//
// ⚠ 2026-09-08 — THIS PAGE DESCRIBED THE MARKETING BOX AS ALSO LETTING THE SHOP "show you its ads on
// services like Facebook and Instagram", AND THE CAMPAIGN'S MESSAGE FLOW NAMES THIS URL, so the A2P
// reviewer opened it. It contradicted itself twice: "What you type goes to the shop and nowhere
// else" against the ads sentence, and the ads sentence against "never shared with those services".
// It also claimed MORE than the checkbox does — the filed label is "text or email me its news,
// offers and events", nothing about ads — and more than any code does. Removed in both languages.
// Chris's rule for this whole class (2026-09-08): "if open is 'say nothing' well then, that's just
// fine" — silence keeps retargeting and lookalikes open; the day we build ad tooling, the consent
// copy is written then and new signers agree to it.

// The terms page's channel list went the same way: enumerating "text, email or social media" both
// narrows (three named things) and reads as ad targeting next to the carve-out. "Keep in touch
// with" alone is broader and says nothing.
//
// TW-0082 — the same page in Spanish when the form's link says `?lang=es` (this page opens in a
// new tab and knows nothing of the form's switch, so the language rides the URL; i18n.ts). The
// English below is the page written on 2026-09-02 plus ONE sentence (2026-09-08, TW-0084: the letters
// typed into the street box go to Google for the address suggestions, and nothing else does — the
// effective date moved with it); the Spanish is a Fable session's translation of it, awaiting a
// native-speaker read as well as the legal one.
//
// REDRAFTED 2026-10-16 AS A LEGAL DOCUMENT, WITH NOTHING WE DO CHANGED (TW-0249, the third document; drafted by a builder from
// Grace's brief, docs/briefs/2026-10-16-the-privacy-page-and-the-consent-forms-page-redrafted-as-legal-documents.md, and
// shipped only after Chris has read it in chat). THE WORDS LIVE IN consentPage.ts NOW, both languages, so a test can read
// them; this file draws them and keeps this history. Chris, 2026-09-29, of the deposits terms' first cut: "seems cheeky and
// not legal is my first immediate feedback", and of the register this page shared with the waivers.tattoo terms: "i haven't
// reviewed the waivers terms in a while and i'm not a lawyer". So the title is the document's own name, "{shop} Consent
// Form Terms and Conditions" (the name the terms box and the address already give it), the two parties and the defined
// terms come first (the Shop, waivers.tattoo, you, the Work, the Record, the Signed Copy), and the sections are numbered under
// conventional headings with lettered clauses: "What you are telling the shop" is 1 (Your Representations), "The release" 2
// (Assumption of Risk and Release), "Your information" 3, "Hearing from the shop" 4 (Hearing from the Shop, the heading the
// carriers' filings name), "Age" 5 and "The rest" 6 (General). docs/legal/consent-page-clause-map-2026-10-16.md carries every
// sentence of the page as it stood to the clause that makes it now. Two parties still, the marketing consent still intent,
// no ads sentence still. ONE SENTENCE IS CORRECTED, NOT KEPT: "This page runs no advertising pixel, no analytics and no
// third-party scripts" stopped being true on 2026-09-08 (TW-0131: the form counts its own page loads and errors, this page's
// included), so 3(d) now says what the privacy policy has said since 2026-09-23. The terms' two general clauses that bind
// nobody came with the register: severability in their own sentence (this page's "the rest still stands") and headings
// (6(e)). The effective date, September 8, 2026, stands with "clarified October 16, 2026" beside it. The consent sentences a
// client ticks are not this page and did not move (consent-sentences-es.json, service.ts, i18n.ts; the test holds their bytes).
//
// CORRECTED THE SAME DAY TO WHAT THE PRODUCT DOES (TW-0249, the fourth slice; Grace's brief,
// docs/briefs/2026-10-16-the-privacy-policy-and-the-consent-page-say-what-the-product-does-today.md). Chris, that morning:
// "But in general or always if we build product features or code we can always update those documents, rather than let those
// documents limit us. I thought we had noted that." · "Our privacy and terms follow the business and product, not the other way
// around." So the second party is the company the terms and the privacy policy name (config.tsx LEGAL_ENTITY, held to the
// root entity.json), "waivers.tattoo" its short name; 3(a) names everything the form can collect (the test holds it to
// Form.tsx's own fields); 3(c) names the AI assistants among who can see a Record; 3(d) says the count is a random key per
// page load, in the privacy policy's words; and 3(e) says where the health answers are kept, the Shop's export and the
// client's own copy included. Nothing a client agreed to moved, so the date line stands. And the page draws nothing while
// the shop is still App.tsx's placeholder, so a client never reads a heading with no shop in it (read back on prod that
// morning). docs/legal/consent-page-clause-map-2026-10-16.md's last section has each, old beside new.

/** One piece of a paragraph: text as it is, or a link, which opens in its own tab as the privacy link always has. */
function Words({ piece }: { piece: ConsentPiece }) {
  if (typeof piece === "string") return <>{piece}</>;
  return (
    <a href={piece.href} className="underline" target="_blank" rel="noreferrer">
      {piece.text}
    </a>
  );
}

/** One paragraph: its clause's letter when it has one, then its pieces. */
function Paragraph({ paragraph }: { paragraph: ConsentParagraph }) {
  return (
    <p className="text-xl mt-4 mb-4">
      {paragraph.clause ? `(${paragraph.clause}) ` : null}
      {paragraph.pieces.map((piece, i) => (
        <Words key={i} piece={piece} />
      ))}
    </p>
  );
}

function TermsAndConditions() {
  const { client } = useApp();

  // The language this page is in — resolved ABOVE the early return below, because a hook may not
  // sit after one. `client` arrives a beat late and enabledLangs reads a missing shop as both (the
  // default since 2026-09-22); nothing renders until the shop lands, so this settles on that render.
  const lang = detectLang(undefined, undefined, enabledLangs(client?.public_data));
  // TW-0094 — the document declares the language it is actually in. This page is what a client is
  // agreeing to, and a screen reader picks its pronunciation from `<html lang>`, which the build
  // bakes to "en".
  useEffect(() => setDocumentLang(lang), [lang]);

  // TW-0249 (2026-10-16) — and nothing while the shop is still App.tsx's placeholder (`name: ""`, until the static config or
  // the API's /shop lands): the document names the Shop as a party, and drawn on the placeholder its heading had no shop
  // in it and its opening sentence read "2 parties: (the “Shop”)" for a beat (read back on prod that morning).
  if (!client || client.placeholder) {
    return null;
  }

  const shop = client.name;

  // TW-0059 — this page is the shop's, not one visit's: it is opened from a checkbox and knows
  // nothing about what the client picked today, so it names what the SHOP does. TW-0279 — what the
  // shop does is its services (service.ts servicesOf: its type, and cosmetic tattooing on its own key).
  // Spanish only when the shop offers it (i18n.ts enabledLangs; on unless turned off since 2026-09-22):
  // a `?lang=es` link on a shop that turned Spanish off reads the English page.
  const page = consentPage(lang, shop, servicesOf(client.public_data));
  const logoUrl = client.public_data?.logo_url;

  return (
    <BasePage>
      <div>
        {logoUrl && (
          <a href="/">
            <img src={logoUrl} alt={shop} className="mx-auto" style={{ width: 200 }} />
          </a>
        )}
        <div className="text-center">
          <div className="px-4">
            <h1 className="text-3xl font-bold">{page.title}</h1>
            <p className="text-lg mt-2">{page.dateLine}</p>
          </div>
        </div>
      </div>
      <div className="mx-auto p-2 sm:p-8">
        <div className="prose">
          {page.intro.map((paragraph, i) => (
            <Paragraph key={`intro-${i}`} paragraph={paragraph} />
          ))}
          {page.sections.map((section, i) => (
            <Fragment key={section.title}>
              <h2 className="text-2xl font-bold mt-8 mb-4">{`${i + 1}. ${section.title}`}</h2>
              {section.paragraphs.map((paragraph, j) => (
                <Paragraph key={j} paragraph={paragraph} />
              ))}
            </Fragment>
          ))}
        </div>
      </div>
    </BasePage>
  );
}

export default TermsAndConditions;
